import { useQuery } from '@tanstack/react-query';
import { Check, ChevronDown, Plus, SearchLg, XClose } from '@untitledui/icons';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button as AriaButton } from 'react-aria-components';
import { useListData } from 'react-stately';

import { usePermissions } from '@/hooks/usePermissions';
import { BadgeWithButton } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { useFilePicker } from '@/lib/base/file-upload/use-file-picker';
import { Input as InputField } from '@/lib/base/forms/input/input';
import {
  MultiSelect,
  type RenderTagFn,
} from '@/lib/base/forms/select/multi-select';
import {
  CHEVRON_SIZE_CLASSES,
  SELECT_TRIGGER_BASE,
  SELECT_TRIGGER_FOCUS,
  type SelectItemType,
  type SelectSize,
  sizes,
} from '@/lib/base/forms/select/select';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { Dropdown } from '@/lib/base/overlays/dropdown';
import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import { createCRMObjectClient } from '@/lib/crm/core/CRMObjectClient';
import type {
  DraftEntity,
  DraftParentInfo,
} from '@/lib/crm/core/draftEntities';
import { buildOptimisticCrmObject } from '@/lib/crm/core/optimisticCrmObject';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import { useAssociationTypes } from '@/lib/crm/hooks/useAssociationTypes';
import {
  entityTypesQueryKey,
  fetchEntityTypeSchemas,
} from '@/lib/crm/hooks/useEntityTypeSchema';
import { usePropertySchema } from '@/lib/crm/hooks/usePropertySchema';
import { useStageTypeNames } from '@/lib/crm/hooks/useStageTypeNames';
import { useStoreObjects } from '@/lib/crm/store';
import {
  getObject,
  getObjectSeq,
  removeObject,
  upsertSummaryObjects,
} from '@/lib/crm/store/crmObjectStore';
import {
  buildEntityKindMap,
  type EntityTypeSchemaLike,
  resolveAssociationTargetType,
} from '@/lib/crm/utils/associationMatching';
import { isCreatableEntityKind } from '@/lib/crm/utils/creatableKinds';
import {
  deriveCreateAssociationNames,
  deriveCreatePropertyNames,
} from '@/lib/crm/utils/genericCreateConfig';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import { getAssociationTargetIconComponent } from '@/lib/crm/utils/icons';
import { isUiCreatableEntityType } from '@/lib/crm/utils/uiCreatableTargets';
import { fileService, FileUploadError } from '@/lib/files/service';
import { useLatestRef } from '@/lib/hooks/useLatestRef';
import { logger } from '@/lib/logger';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';
import { cx } from '@/utils/cx';

import type { EntityTypeName } from '../../core/types';
// Direct module paths, NOT the `lib/crm/create` barrel: the barrel pulls in
// the provider, which renders the create form, which renders this picker.
import { buildGenericCreateConfig } from '../../create/buildGenericCreateConfig';
import { useCreateSlideout } from '../../create/CreateSlideoutContext';
import { useCreateCrmObject } from '../../hooks/useCreateCrmObject';
import {
  getEntityTypeConfig,
  getEntityTypeCreateConfig,
} from '../../record/registry/entityTypeRegistry';
import { CrmObjectAvatar } from '../CrmObjectAvatar';
import {
  buildDraftEntity,
  buildDraftSummaryObject,
} from './draftEntityCapture';

/**
 * Minimal properties needed for display name + avatar rendering,
 * keyed by entity type. Each list includes only properties that
 * actually exist on that type to avoid fetching non-existent fields.
 *
 * Covers: getDisplayName (ahoy_display_name → ahoy_name fallback)
 *         CrmObjectAvatar (initials, Gravatar, favicon, profile photo)
 */
const DISPLAY_PROPERTIES_BY_TYPE: Record<string, string[]> = {
  ahoy_contact: [
    'ahoy_display_name',
    'ahoy_first_name',
    'ahoy_last_name',
    'ahoy_email',
    'ahoy_profile_photo_url',
  ],
  ahoy_company: ['ahoy_display_name', 'ahoy_name', 'ahoy_domain'],
  ahoy_user: [
    'ahoy_display_name',
    'ahoy_first_name',
    'ahoy_last_name',
    'ahoy_avatar_url',
  ],
  ahoy_deal: ['ahoy_display_name', 'ahoy_name'],
};

/** Fallback for entity types not in the map (custom types, notes, etc.) */
const DEFAULT_DISPLAY_PROPERTIES = ['ahoy_display_name', 'ahoy_name'];

// Sizing comes from the shared field-control scale (`sizes` /
// `CHEVRON_SIZE_CLASSES`) — this picker used to carry its own table that ran
// one notch tighter than the library, so the same `size` prop produced a
// different height here than on a Select beside it, and the multi-select path
// (which delegates its trigger to the library MultiSelect) needed a mapping
// table to compensate. Both are gone: one scale, no translation.

export interface AssociatedCrmObjectInputProps {
  /** Association type schema */
  associationType: AssociationType;
  /**
   * Pins a kind-based association to ONE of the entity types it resolves to,
   * so the picker offers only that type. Set when the field renders a single
   * per-type subset of the rule (see `utils/associationTargetSubsets.ts`).
   */
  targetEntityTypeName?: string;
  /** Current value - always an array of entity_ids */
  value: string[] | null | undefined;
  /** Callback when value changes */
  onChange: (value: string[] | null) => void;
  /** Org ID for API calls */
  orgId: number;
  /** Whether input is disabled */
  disabled?: boolean;
  /** Source object's entity_id - used to prepopulate reverse association when creating */
  sourceEntityId?: string;
  /** Optional guard callback before opening create modal. Call proceed() to continue. */
  onBeforeCreate?: (proceed: () => void) => void;
  /** Whether a saved value exists (for *_to_one, hides create option if true) */
  hasSavedValue?: boolean;
  /** Hide the "Create new …" option entirely (e.g. in filter contexts) */
  hideCreateOption?: boolean;
  /**
   * Suppress the search box and results list, leaving only the create
   * affordance — for targets where attaching an EXISTING record is never a
   * valid operation, so offering the picker can only cause harm.
   *
   * The motivating case is secrets: a secret entity belongs to exactly one
   * principal for its whole life, and `token_resolver` requires exactly one
   * live principal per secret (`token_resolver.py:110`). Attaching a secret
   * that is already bound elsewhere silently breaks BOTH credentials' tokens —
   * the resolver returns None for the ambiguity, and `ahoy_secret_hint` is
   * write-once so the entity cannot be repaired afterwards.
   *
   * Distinct from `hideCreateOption`, its exact inverse. Pair this with
   * `onCustomCreate` — with the picker gone, that handler is the only way to
   * populate the row.
   */
  createOnly?: boolean;
  /** Callback when selected objects change - maps entity_id to CrmObject */
  onSelectedObjectsChange?: (objects: Record<string, CrmObject>) => void;
  /**
   * Custom create handler — when set, the "Create new" button calls this
   * instead of opening the standard create slideout.
   * Used for entity types that need non-standard create flows (e.g. secrets).
   */
  onCustomCreate?: () => void;
  /**
   * When present, the nested Create-new-X modal produces a client-side
   * DraftEntity via this callback instead of eagerly creating a server
   * record; the parent form holds it and submits atomically. Absence =
   * today's eager create. See #11362.
   */
  onCreateDraft?: (draft: DraftEntity) => void;
  /**
   * Parent-identity context threaded down from a parent draft form so a
   * nested create can render a "belongs to {parent}" banner.
   */
  draftParentInfo?: DraftParentInfo;
  /**
   * Size variant — controls trigger height, padding, and text size, on the
   * shared field-control scale. Defaults to `'sm'` (40px), the height used by
   * record create/edit forms.
   */
  size?: SelectSize;
  /** Additional className applied to the outermost container. */
  className?: string;
  /** Additional className applied to the trigger button (single-select). */
  triggerClassName?: string;
  /**
   * Open the picker dropdown immediately on mount. Used by table-cell
   * editing, where entering edit mode should drop the user straight into
   * the search — clicking the trigger a second time is pure friction.
   */
  defaultOpen?: boolean;
  /**
   * When true, conflicting items (already associated with another source) are
   * selectable instead of disabled. The hint text is softened to indicate the
   * conflict rather than blocking the action. Conflict resolution is then
   * delegated to the server-side validate hook.
   */
  allowOverwrite?: boolean;
  /**
   * Overrides how many values may be selected, independent of association
   * cardinality. `'auto'` (default) keeps the cardinality-driven behavior:
   * single-select for `many_to_one`/`one_to_one`, multi-select otherwise.
   * `'single'` forces single-select; `'multiple'` forces multi-select.
   * Used by filter clauses where the operator (Is / Is Any Of), not the
   * association cardinality, determines the number of values.
   */
  selectionMode?: 'auto' | 'single' | 'multiple';
  /**
   * Rendering variant. `'default'` renders a trigger button that opens a
   * dropdown popover. `'embedded'` (single-select only) renders the picker
   * body inline — selected-value chip, search input, and option list stacked
   * in the parent's own surface, with no trigger and no popover. Used by
   * table-cell editing, where the cell overlay already provides the card and
   * a nested trigger + floating popover reads as two disjoint layers.
   */
  variant?: 'default' | 'embedded';
  /**
   * Whether the current value may be cleared (single-select only). When
   * false, the "No <type>" clear row and the trigger's ✕ button are hidden —
   * for required single associations like a pipeline/lifecycle stage, which
   * an entity must always have. Defaults to true.
   */
  allowClear?: boolean;
  /**
   * Trigger text when nothing is selected (single-select only). Defaults to
   * `Select <association label>`.
   *
   * Override it where "nothing selected" is a MEANING rather than a missing
   * answer. On a filter bar an empty picker is not an unanswered question —
   * it is the statement "this filter is not narrowing anything" — and an
   * imperative there reads as though the data is waiting on the viewer.
   */
  emptyLabel?: string;
}

export const AssociatedCrmObjectInput: React.FC<
  AssociatedCrmObjectInputProps
> = ({
  associationType,
  targetEntityTypeName,
  value,
  onChange,
  orgId,
  disabled = false,
  sourceEntityId,
  onBeforeCreate,
  hasSavedValue: _hasSavedValue = false,
  hideCreateOption = false,
  createOnly = false,
  onSelectedObjectsChange,
  onCustomCreate,
  onCreateDraft,
  draftParentInfo,
  size = 'sm',
  className: containerClassName,
  triggerClassName,
  allowOverwrite = false,
  selectionMode = 'auto',
  defaultOpen = false,
  variant = 'default',
  allowClear = true,
  emptyLabel,
}) => {
  // Fetch object types to build stage type name set for sort-order detection
  const { data: objectTypesData } = useQuery({
    queryKey: entityTypesQueryKey(orgId),
    queryFn: () => fetchEntityTypeSchemas(orgId),
    enabled: !!orgId,
    staleTime: 5 * 60 * 1000,
  });
  const { stageTypeNames } = useStageTypeNames(orgId);

  // Internal state
  const [isOpen, setIsOpen] = useState(defaultOpen && !disabled);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [fetchState, setFetchState] = useState({
    loading: false,
    targetObjects: [] as CrmObject[],
    associatedObjects: {} as Record<string, CrmObject>,
  });
  // File upload (ahoy_file targets) — declared here so createOptionText
  // (below) can reference isUploading before the fuller upload logic further
  // down the component body.
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  // Must mirror the isOpen initializer: the options fetch below is gated on
  // hasEverOpened, and neither a defaultOpen mount nor the embedded variant
  // (which has no dropdown at all) ever fires onOpenChange — without this the
  // picker opens onto a permanently empty "No … found" list.
  //
  // The embedded variant always fetches on mount, even when disabled: it has
  // to render the current selection (a checked row), and gating the fetch on
  // `!disabled` would leave a picker that mounted disabled permanently empty
  // once it's enabled. Editing while disabled is prevented at the interaction
  // layer instead (buttons/keydown short-circuit below). The default-variant
  // auto-open still respects `disabled`.
  const [hasEverOpened, setHasEverOpened] = useState(
    variant === 'embedded' || (defaultOpen && !disabled)
  );
  // For kind-based associations (e.g. ahoy_note_to_contact targets kind
  // "person" instead of a specific entity type), use entity_kind for API
  // queries and resolve the first matching type for labels/permissions.
  const kindName = associationType.target_entity_kind_name;
  // `targetEntityTypeName` pins a kind-based rule to ONE of the types it
  // resolves to — the field is rendering that type's subset, so its picker must
  // offer only that type. With it set the rule behaves exactly like a
  // type-based one here.
  const isKindBased =
    !associationType.target_entity_type_name &&
    !!kindName &&
    !targetEntityTypeName;
  // Memoize so the effects below can include entityRef in their dep arrays
  // without re-fetching on every render — kind-based refs are objects and
  // would otherwise get a new identity on each render.
  const entityRef: string | { entity_kind: string } = useMemo(
    () =>
      isKindBased && kindName
        ? { entity_kind: kindName }
        : (targetEntityTypeName ?? associationType.target_entity_type_name),
    [
      isKindBased,
      kindName,
      targetEntityTypeName,
      associationType.target_entity_type_name,
    ]
  );
  // Kind-based queries hit the backend with `{ entity_kind: ... }`, which
  // returns every entity of that kind — including types the association schema
  // marks as excluded (e.g. `ahoy_user` for `person` on `ahoy_contact_to_note`).
  // The backend has no per-association exclusion filter for entity_kind queries,
  // so we drop excluded types on the client to keep the picker consistent with
  // the schema.
  const excludedEntityTypeNames = useMemo(
    () => associationType.target_excluded_entity_type_names ?? [],
    [associationType.target_excluded_entity_type_names]
  );
  const filterExcludedTypes = useCallback(
    (results: CrmObject[]): CrmObject[] => {
      if (!isKindBased || excludedEntityTypeNames.length === 0) {
        return results;
      }
      const excluded = new Set(excludedEntityTypeNames);
      return results.filter(obj => !excluded.has(obj.entity_type_name));
    },
    [isKindBased, excludedEntityTypeNames]
  );
  const containerRef = useRef<HTMLDivElement>(null);

  // Resolve the concrete entity type for the target. For type-based
  // associations, target_entity_type_name is the type directly. For
  // kind-based associations (e.g. ahoy_note_to_contact targets kind
  // 'person'), resolve by finding the first matching object type whose
  // entity_kind tuple includes the kind, excluding excluded types.
  const entityType = useMemo(
    () =>
      targetEntityTypeName ??
      resolveAssociationTargetType(
        associationType,
        objectTypesData as EntityTypeSchemaLike[] | undefined
      ),
    [targetEntityTypeName, associationType, objectTypesData]
  );

  // A surface-supplied onCustomCreate takes precedence over the built-in
  // upload affordance, matching the existing precedence for the generic
  // create path.
  const isFileUploadTarget =
    !onCustomCreate && associationType.target_entity_type_name === 'ahoy_file';

  // Read selected objects reactively from the centralized store. Objects land
  // there from the detail endpoint's associated_objects, list queries, and WS
  // events — so they're usually already present without an extra fetch.
  const valueIds = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const selectedObjects = useStoreObjects(valueIds);

  // Derived loading state: only applies to dropdown options, not the closed trigger
  const isOptionsLoading = fetchState.loading;

  // Derive entityTypeLabel from the resolved entity type
  const entityTypeLabel = useMemo(() => {
    if (!entityType) {
      return null;
    }
    const typeMapping = objectTypesData?.find(
      t => t.entity_type_name === entityType
    );
    return typeMapping?.label ?? null;
  }, [objectTypesData, entityType]);
  const { open: openCreateSlideout } = useCreateSlideout();
  const { hasPermission } = usePermissions();
  // Per-user permission gate — applies to EVERY create path, including a
  // caller-supplied onCustomCreate handler.
  const canCreateTargetObject = hasPermission(
    entityType,
    'ahoy_object_created'
  );
  // Two additional gates that apply only to the GENERIC "Create new X" path
  // (a caller-supplied onCustomCreate handler is a bespoke flow — e.g. an
  // invite-user or create-secret dialog — and is not second-guessed here):
  // (1) the schema-level user_creatable flag (false for system-managed types
  // like users/email threads, whose generic creates the backend rejects;
  // missing field — cached pre-rollout schema — is treated as creatable, the
  // backend enforces), and (2) the UI-create blocklist (stage / user targets
  // are made elsewhere — pipeline config, invitations).
  const targetTypeRecord = objectTypesData?.find(
    t => t.entity_type_name === entityType
  );
  const targetTypeUserCreatable = targetTypeRecord?.user_creatable !== false;
  const targetKinds = [
    ...(targetTypeRecord?.entity_kind ?? []),
    associationType.target_entity_kind_name,
  ].filter((kind): kind is string => kind != null);
  const genericCreateTargetAllowed =
    targetTypeUserCreatable && isUiCreatableEntityType(entityType, targetKinds);

  // Single- vs multi-select is normally driven by association cardinality
  // (*_to_one => single). `selectionMode` lets a caller (e.g. a filter
  // clause) override that based on the operator instead of the cardinality.
  let isSingleSelect: boolean;
  if (selectionMode === 'single') {
    isSingleSelect = true;
  } else if (selectionMode === 'multiple') {
    isSingleSelect = false;
  } else {
    isSingleSelect =
      associationType.cardinality === 'many_to_one' ||
      associationType.cardinality === 'one_to_one';
  }
  const isMulti = !isSingleSelect;
  const maxSelection = isSingleSelect ? 1 : undefined;

  // Get icon for this association target — kind-aware so kind-only
  // associations show the kind icon instead of an arbitrary matching type's icon
  const Icon = useMemo(
    () => getAssociationTargetIconComponent(associationType),
    [associationType]
  );

  // Get plural label for placeholder (e.g., "companies" not "company")
  const pluralLabel = useMemo(() => {
    const config = getEntityTypeConfig(entityType);
    return config.pluralDisplayName.toLowerCase();
  }, [entityType]);

  // Get the static registry create config, if any (contact/company/deal).
  const staticCreateConfig = useMemo(
    () => getEntityTypeCreateConfig(entityType),
    [entityType]
  );

  // Schema-driven fallback create config so inline "Create new …" works for ANY
  // entity type the user can create — not just the three registry types. We
  // derive the property/association name lists from the live schema (mirroring
  // GenericCrmList); CrmCreateForm is entity-type-agnostic and renders them.
  const { schema: targetPropertySchema } = usePropertySchema(orgId, entityType);
  const { associationTypes: allAssociationTypes } = useAssociationTypes(orgId);
  const entityKindMap = useMemo(
    () => buildEntityKindMap(objectTypesData ?? []),
    [objectTypesData]
  );
  const derivedCreateConfig = useMemo(
    () => ({
      createProperties: deriveCreatePropertyNames(targetPropertySchema),
      createAssociations: deriveCreateAssociationNames(
        allAssociationTypes,
        entityType,
        entityKindMap
      ),
      // Prefill the record name from the search text the user typed, if any.
      parseSearchText: (text: string): Record<string, unknown> =>
        text.trim() ? { ahoy_display_name: text.trim() } : {},
    }),
    [targetPropertySchema, allAssociationTypes, entityType, entityKindMap]
  );

  // Use the curated static config when present, else the schema-derived one.
  const createConfig = staticCreateConfig ?? derivedCreateConfig;

  // Initialize the create mutation
  const createMutation = useCreateCrmObject(
    orgId,
    entityType as EntityTypeName
  );

  // Compute create option text
  const createOptionText = useMemo(() => {
    if (isFileUploadTarget) {
      return isUploading ? 'Uploading…' : 'Upload file';
    }
    if (onCustomCreate) {
      return `Create new ${entityTypeLabel?.toLowerCase() || 'item'}`;
    }
    const typeLabel = entityTypeLabel?.toLowerCase() || 'item';
    if (searchQuery.trim()) {
      return `Create "${searchQuery.trim()}"`;
    }
    return `Create new ${typeLabel}`;
  }, [
    isFileUploadTarget,
    isUploading,
    onCustomCreate,
    entityTypeLabel,
    searchQuery,
  ]);

  // Handle modal submit
  const handleCreateSubmit = useCallback(
    async (data: Record<string, unknown>) => {
      // CrmCreateForm sends { ...properties, associations?: [...] }
      const { associations, ...properties } = data;
      const createdObject = await createMutation.mutateAsync({
        properties,
        associations:
          (associations as Array<{
            association_type_name: string;
            target_entity_id: string;
          }>) || [],
      });

      // Add newly created object to targetObjects and the centralized store
      upsertSummaryObjects([createdObject]);
      setFetchState(prev => ({
        ...prev,
        targetObjects: [createdObject, ...prev.targetObjects],
      }));

      let newValue: string[];
      if (isSingleSelect) {
        newValue = [createdObject.entity_id];
      } else {
        const currentValue = Array.isArray(value) ? [...value] : [];
        newValue = [...currentValue, createdObject.entity_id];
      }
      onChange(newValue);
      setSearchQuery('');
    },
    [createMutation, value, onChange, isSingleSelect]
  );

  // entity_ids of the seq-0 draft summaries this input has pushed into the
  // global store, so an unmount (parent create modal cancelled or closed) can
  // evict any that were never submitted — see the cleanup effect below.
  const createdDraftIdsRef = useRef<Set<string>>(new Set());

  // Evict a draft chip this input created once its association target is
  // removed (chip cleared / replaced) while the parent form stays open —
  // otherwise its never-submitted seq-0 synthetic lingers in the global store
  // until unmount, showing as a ghost association. Guarded two ways: only ids
  // THIS input created (so removing a real, pre-existing association is
  // untouched), and only while still at seq 0 (a successful parent submit
  // swaps in the real objectCreated at seq >= 1, which must not be removed).
  // The parent draft map needs no explicit removal here: submit collects only
  // drafts still referenced by an association value (selectReferencedDrafts),
  // so an unreferenced draft is already excluded (KD2).
  const evictCreatedDraft = useCallback((id: string) => {
    if (createdDraftIdsRef.current.has(id) && getObjectSeq(id) === 0) {
      removeObject(id);
      setFetchState(prev => ({
        ...prev,
        targetObjects: prev.targetObjects.filter(
          object => object.entity_id !== id
        ),
      }));
      createdDraftIdsRef.current.delete(id);
    }
  }, []);

  // Handle modal submit in draft mode — builds a client-side DraftEntity
  // instead of eagerly creating a server record, and hands it to the parent
  // via onCreateDraft. No optimistic seq recording: the parent submits the
  // draft atomically alongside its own create.
  const handleCreateDraftSubmit = useCallback(
    async (data: Record<string, unknown>) => {
      const draft = buildDraftEntity(entityType as string, data);
      onCreateDraft?.(draft);
      const summary = buildDraftSummaryObject(draft, orgId);
      // Single-select replaces the value: evict any prior draft chip this input
      // created so its now-unreferenced seq-0 summary doesn't linger as a ghost
      // (mirrors handleItemInserted's single-select replacement eviction).
      if (isSingleSelect) {
        for (const id of Array.isArray(value) ? value : []) {
          if (id !== draft.entity_id) {
            evictCreatedDraft(id);
          }
        }
      }
      createdDraftIdsRef.current.add(draft.entity_id);
      upsertSummaryObjects([summary]); // seq-0 chip; NO addPendingCreation/applyOptimisticBatch
      setFetchState(prev => ({
        ...prev,
        targetObjects: [summary, ...prev.targetObjects],
      }));
      const newValue = isSingleSelect
        ? [draft.entity_id]
        : [...(Array.isArray(value) ? value : []), draft.entity_id];
      onChange(newValue);
      setSearchQuery('');
    },
    [
      entityType,
      orgId,
      onCreateDraft,
      isSingleSelect,
      value,
      onChange,
      evictCreatedDraft,
    ]
  );

  // On unmount (the parent create form was cancelled or closed), evict any
  // draft chips this input created that were never submitted. A successful
  // parent submit replaces the seq-0 synthetic with the real objectCreated
  // (seq >= 1), so we remove ONLY entries still at seq 0 — otherwise repeated
  // create-then-cancel cycles leave phantom seq-0 objects lingering in the
  // global store until LRU eviction.
  useEffect(() => {
    const createdDraftIds = createdDraftIdsRef.current;
    return () => {
      for (const id of createdDraftIds) {
        if (getObjectSeq(id) === 0) {
          removeObject(id);
        }
      }
    };
  }, []);

  // Compute initial values for modal (memoized)
  // Includes parsed search text AND the reverse association (to prepopulate link back to source)
  const createModalInitialValues = useMemo(() => {
    if (!createConfig) {
      return {};
    }
    const parsedValues = createConfig.parseSearchText(searchQuery);

    // If we have a source object, prepopulate the reverse association
    // e.g., creating Contact from Deal's contact field → prepopulate contact_to_deal with deal's entity_id
    if (sourceEntityId && associationType.reverse_association_type_name) {
      return {
        ...parsedValues,
        [associationType.reverse_association_type_name]: [sourceEntityId],
      };
    }

    return parsedValues;
  }, [
    createConfig,
    searchQuery,
    sourceEntityId,
    associationType.reverse_association_type_name,
  ]);

  // Handle create option click — pushes a nested create form onto the create
  // slideout stack, or calls onCustomCreate for non-standard create flows
  // (e.g. secrets).
  const handleCreateClick = useCallback(() => {
    if (onCustomCreate) {
      setIsOpen(false);
      onCustomCreate();
      return;
    }
    const nestedOnSubmit = onCreateDraft
      ? handleCreateDraftSubmit
      : handleCreateSubmit;
    // Only threaded on the draft branch — eager (server) nested creates have
    // no parent draft to describe a "belongs to" banner for.
    const draftParentContext =
      onCreateDraft && draftParentInfo
        ? {
            ...draftParentInfo,
            reverseAssociationTypeName:
              associationType.reverse_association_type_name,
          }
        : undefined;
    const openCreate = () => {
      if (!createConfig) {
        return;
      }
      const createTitle = `Create ${(entityTypeLabel || 'item').toLowerCase()}`;
      openCreateSlideout(
        buildGenericCreateConfig({
          orgId,
          entityType,
          title: createTitle,
          createProperties: createConfig.createProperties,
          createAssociations: createConfig.createAssociations,
          onSubmit: nestedOnSubmit,
          submitLabel: createTitle,
          initialValues: createModalInitialValues,
          showCreateAnother: false,
          draftParentContext,
        })
      );
    };
    setIsOpen(false);
    if (onBeforeCreate) {
      onBeforeCreate(openCreate);
    } else {
      openCreate();
    }
  }, [
    onCustomCreate,
    onBeforeCreate,
    createConfig,
    entityType,
    entityTypeLabel,
    onCreateDraft,
    handleCreateDraftSubmit,
    handleCreateSubmit,
    createModalInitialValues,
    orgId,
    openCreateSlideout,
    draftParentInfo,
    associationType.reverse_association_type_name,
  ]);

  // ---- Fetch 1: Resolve selected values on mount (lightweight) ----
  // useStoreObjects reads reactively from the store. This effect only fires
  // when some values are NOT in the store yet (e.g., first load before the
  // detail endpoint has resolved associated_objects).
  useEffect(() => {
    if (!sourceEntityId || valueIds.length === 0) {
      return;
    }
    // Kind-based associations resolve `entityType` from object-type schemas
    // that load asynchronously. Skip fetching until it's a real type, otherwise
    // we issue a request against an empty type and 404.
    if (!entityType) {
      return;
    }

    // Skip fetch if all selected values are already in the store
    const allInStore = valueIds.every(id => {
      const obj = getObject(id);
      return (
        !!obj &&
        (!!getPropertyValue(obj.properties?.ahoy_display_name) ||
          !!getPropertyValue(obj.properties?.ahoy_name))
      );
    });
    if (allInStore) {
      return;
    }

    const controller = new AbortController();

    const fetchSelected = async () => {
      try {
        const client = createCRMObjectClient(orgId, entityRef);
        const response = await client.list(
          {
            associated_with_entity_id: sourceEntityId,
            properties:
              DISPLAY_PROPERTIES_BY_TYPE[entityType] ??
              DEFAULT_DISPLAY_PROPERTIES,
            page: 1,
            page_size: 100,
          },
          { signal: controller.signal }
        );
        // Merge into centralized store — useStoreObjects re-renders automatically
        upsertSummaryObjects(filterExcludedTypes(response.results));
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        logger.warn('Failed to fetch selected association objects', {
          entityType,
          error: err,
        });
      }
    };

    fetchSelected();
    return () => controller.abort();
    // Only run on mount — value changes are handled by the options list
    // which is already loaded once the dropdown has opened. entityRef is
    // included so a kind-based association picks up a new target when the
    // resolved kind changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, entityType, entityRef, sourceEntityId, filterExcludedTypes]);

  // ---- Fetch 2: Options list (deferred until dropdown opens) ----
  // Defer the full options fetch until the user actually opens the dropdown.
  // Exception: when there are pre-selected values but no sourceEntityId (filter
  // context), we need the options list to resolve display names since Fetch 1
  // requires sourceEntityId to work.
  const hasPreSelectedValues =
    Array.isArray(value) && value.length > 0 && !sourceEntityId;
  useEffect(() => {
    if (!hasPreSelectedValues && !hasEverOpened) {
      return;
    }
    // Kind-based associations resolve `entityType` from object-type schemas
    // that load asynchronously. Skip fetching until it's a real type — otherwise
    // we'd issue an initial list query against an empty type name.
    if (!entityType) {
      return;
    }

    const controller = new AbortController();

    const fetchTargets = async () => {
      // Only show loading on initial load, not during search typing
      const isInitialLoad = fetchState.targetObjects.length === 0;

      if (isInitialLoad) {
        setFetchState(prev => ({ ...prev, loading: true }));
      }

      try {
        const client = createCRMObjectClient(orgId, entityRef);
        // Stage entity types should be sorted by order, not by name/created_at
        const isStageType = stageTypeNames.has(entityType);
        const response = await client.list(
          {
            search: searchQuery || undefined,
            page: 1,
            page_size: 20,
            sort: isStageType ? 'ahoy_order' : undefined,
            order: isStageType ? 'asc' : undefined,
            // Request associated objects for one_to_many cardinality to detect conflicts
            // Only needed when editing a specific source object (sourceEntityId provided)
            include_associated_objects:
              isMulti && sourceEntityId ? true : undefined,
          },
          { signal: controller.signal }
        );
        const filteredResults = filterExcludedTypes(response.results);
        // Merge fetched objects into the centralized store
        upsertSummaryObjects(filteredResults);
        if (response.associated_objects) {
          upsertSummaryObjects(Object.values(response.associated_objects));
        }
        // Update loading, targetObjects, and associatedObjects atomically
        setFetchState({
          loading: false,
          targetObjects: filteredResults,
          associatedObjects: response.associated_objects || {},
        });
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        logger.warn('Failed to fetch target objects', {
          entityType,
          error: err,
        });
        // Update all state atomically on error
        setFetchState({
          loading: false,
          targetObjects: [],
          associatedObjects: {},
        });
      }
    };

    fetchTargets();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchState.targetObjects.length cannot be in deps: this effect updates targetObjects, so including its length causes an infinite re-fetch loop
  }, [
    searchQuery,
    orgId,
    entityType,
    entityRef,
    isMulti,
    sourceEntityId,
    stageTypeNames,
    hasEverOpened,
    hasPreSelectedValues,
    filterExcludedTypes,
  ]);

  // Display name utility — imported getDisplayName is a pure function, no wrapper needed

  // Check if an object has a conflicting reverse association (associated with a different source)
  // This applies to one_to_many/many_to_many associations where the target has a many_to_one reverse
  const getConflictingAssociation = useCallback(
    (obj: CrmObject): { isConflict: boolean; targetLabel?: string } => {
      // Conflict detection only applies when editing a specific source object's
      // associations (sourceEntityId provided). In filter or create contexts,
      // there is no source object to conflict with, so all items are selectable.
      if (!sourceEntityId) {
        return { isConflict: false };
      }

      // Only check for conflicts if this is a one_to_many or many_to_many association
      if (!isMulti) {
        return { isConflict: false };
      }

      // If no associations data, can't determine conflict - allow selection
      if (!obj.associations || obj.associations.length === 0) {
        return { isConflict: false };
      }

      // Get the reverse association type name
      const reverseAssocTypeName =
        associationType.reverse_association_type_name;

      // Find if the object has the reverse association
      const reverseAssoc = obj.associations.find(
        a => a.association_type_name === reverseAssocTypeName
      );

      if (!reverseAssoc) {
        return { isConflict: false };
      }

      // Check if the object is associated with a DIFFERENT source (not in current value)
      const currentValue = Array.isArray(value) ? value : [];

      // If the target is already in our value (associated with us), no conflict
      if (currentValue.includes(obj.entity_id)) {
        return { isConflict: false };
      }

      // The object has a reverse association to something else
      // Look up the associated object's label from the associatedObjects dict
      const associatedObj =
        fetchState.associatedObjects[reverseAssoc.target_entity_id];
      const targetLabel = associatedObj
        ? getDisplayName(associatedObj)
        : undefined;

      // Only mark as conflict if we have the associated_objects data to show who it's associated with
      // This ensures graceful fallback when data is unavailable
      if (!associatedObj) {
        return { isConflict: false };
      }

      return { isConflict: true, targetLabel };
    },
    [
      isMulti,
      associationType.reverse_association_type_name,
      value,
      fetchState.associatedObjects,
      sourceEntityId,
    ]
  );

  // Lookup map from entity_id to CrmObject for avatar rendering.
  // Merges lightweight selected objects (from mount fetch) with full options
  // list (from dropdown fetch). Options list objects take precedence since
  // they have complete property data.
  const targetObjectsMap = useMemo(() => {
    const map = new Map<string, CrmObject>();
    // Add selected objects first (lightweight, from mount fetch)
    for (const obj of selectedObjects) {
      map.set(obj.entity_id, obj);
    }
    // Overwrite with full options list objects (richer data)
    for (const obj of fetchState.targetObjects) {
      map.set(obj.entity_id, obj);
    }
    return map;
  }, [selectedObjects, fetchState.targetObjects]);

  // Handler for search input changes (extracted to satisfy stable-reference rule)
  const handleSearchInputChange = useCallback(
    (value: string) => setSearchQuery(value),
    [] // setSearchQuery is a stable state setter
  );

  // Handler for dropdown open — triggers deferred options fetch.
  // Sets loading immediately so the empty state doesn't flash before the
  // effect fires the actual fetch.
  const handleDropdownOpen = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (open && !hasEverOpened) {
        setHasEverOpened(true);
        // Mark loading synchronously so the UI never shows
        // "No items" between open and the effect running.
        if (fetchState.targetObjects.length === 0) {
          setFetchState(prev => ({ ...prev, loading: true }));
        }
      }
      if (!open) {
        setSearchQuery('');
      }
    },
    [hasEverOpened, fetchState.targetObjects.length]
  );

  // Prepare items for select with disabled state and supporting text.
  // Merge selectedObjects (from mount fetch) with targetObjects (from dropdown
  // fetch) so the selected value is never lost during the transition. Once the
  // dropdown has loaded, targetObjects is the primary source — selectedObjects
  // fills the gap for any IDs not yet in targetObjects (e.g. while loading).
  const items = useMemo((): SelectItemType[] => {
    const targetIds = new Set(fetchState.targetObjects.map(o => o.entity_id));
    const merged = [
      ...fetchState.targetObjects,
      ...selectedObjects.filter(o => !targetIds.has(o.entity_id)),
    ];
    // Before the dropdown has opened and we have no targetObjects,
    // fall back to selectedObjects only
    const sourceObjects =
      hasEverOpened || !sourceEntityId ? merged : selectedObjects;
    return sourceObjects.map(obj => {
      const conflict = getConflictingAssociation(obj);
      let conflictHint: string | undefined;
      if (conflict.isConflict) {
        conflictHint = allowOverwrite
          ? `Currently associated with ${conflict.targetLabel || 'another'}`
          : `Already associated with ${conflict.targetLabel || 'another'}`;
      }
      return {
        id: obj.entity_id,
        label: getDisplayName(obj),
        isDisabled: conflict.isConflict && !allowOverwrite,
        supportingText: conflictHint,
      };
    });
  }, [
    hasEverOpened,
    sourceEntityId,
    fetchState.targetObjects,
    selectedObjects,
    getConflictingAssociation,
    allowOverwrite,
  ]);

  // Multi-select state
  const selectedItems = useListData({
    initialItems: Array.isArray(value)
      ? items.filter(item => value.includes(item.id))
      : [],
    getKey: item => item.id,
  });

  // Filter out already selected items for multi-select.
  // Single-select shows all items so the user can see the full list and change selection.
  const availableItems = useMemo(() => {
    if (isSingleSelect) {
      return items;
    }
    return items.filter(
      item => !selectedItems.items.some(s => s.id === item.id)
    );
  }, [items, selectedItems.items, isSingleSelect]);

  // Sync value changes to selectedItems
  useEffect(() => {
    const valueIds = Array.isArray(value) ? [...value] : [];
    const currentIds = selectedItems.items.map(i => i.id);

    // Only sync if IDs actually changed
    if (JSON.stringify(valueIds.sort()) !== JSON.stringify(currentIds.sort())) {
      selectedItems.remove(...currentIds);
      // Use items array which has the full shape including disabled state
      const newItems = items.filter(item => valueIds.includes(item.id));
      selectedItems.append(...newItems);
    }
    // Include items to sync when data loads, but use object identity to avoid re-running on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMulti, value, items]);

  // Track previously reported object IDs to avoid infinite loops
  const reportedObjectIdsRef = useRef<string>('');

  // Shared helper: notify parent of selected objects (deduped by ID set).
  // Called from the sync effect and from insert/clear handlers.
  const notifySelectedObjects = useCallback(
    (ids: string[]) => {
      if (!onSelectedObjectsChange) {
        return;
      }
      const objects: Record<string, CrmObject> = {};
      for (const id of ids) {
        const obj = targetObjectsMap.get(id);
        if (obj) {
          objects[id] = obj;
        }
      }
      const objectIdsKey = Object.keys(objects).sort().join(',');
      if (objectIdsKey !== reportedObjectIdsRef.current) {
        reportedObjectIdsRef.current = objectIdsKey;
        onSelectedObjectsChange(objects);
      }
    },
    [onSelectedObjectsChange, targetObjectsMap]
  );

  // Notify parent of selected objects when items load (for existing values)
  useEffect(() => {
    if (!onSelectedObjectsChange || targetObjectsMap.size === 0) {
      return;
    }
    if (valueIds.length === 0) {
      return;
    }
    notifySelectedObjects(valueIds);
  }, [
    targetObjectsMap,
    valueIds,
    onSelectedObjectsChange,
    notifySelectedObjects,
  ]);

  // Handle item insertion - update parent state
  const handleItemInserted = useCallback(
    (key: React.Key) => {
      const newValue =
        maxSelection === 1
          ? [String(key)]
          : [...(Array.isArray(value) ? value : []), String(key)];
      // Single-select replacement drops the previously selected value; if that
      // was a draft this input created, evict its now-unreferenced seq-0 chip.
      if (maxSelection === 1) {
        for (const id of Array.isArray(value) ? value : []) {
          if (id !== String(key)) {
            evictCreatedDraft(id);
          }
        }
      }
      onChange(newValue);
      setSearchQuery('');
      notifySelectedObjects(newValue);
    },
    [value, onChange, maxSelection, notifySelectedObjects, evictCreatedDraft]
  );

  // Handle item removal - update parent state
  const handleItemCleared = useCallback(
    (key: React.Key) => {
      const newValue = (Array.isArray(value) ? value : []).filter(
        id => id !== String(key)
      );
      evictCreatedDraft(String(key));
      onChange(newValue.length > 0 ? newValue : null);
      notifySelectedObjects(newValue);
    },
    [value, onChange, notifySelectedObjects, evictCreatedDraft]
  );

  // Render custom tag: plain text for single-select, BadgeWithButton for multi-select
  const renderTag: RenderTagFn = useCallback(
    (item, onRemove) => {
      // Single-select: plain text (no badge, no ×)
      if (isSingleSelect) {
        return (
          <span key={String(item.id)} className='truncate text-sm text-primary'>
            {item.label}
          </span>
        );
      }

      // A disabled input must not offer removal — an internal-access
      // association (e.g. the engine-recorded "Created" provenance edge) is
      // shown for its value, and a × here could only produce a rejected
      // write. The value is still worth navigating to, so render the
      // canonical entity badge (click opens the entity's slideout).
      if (disabled) {
        return (
          <EntityBadgeById
            key={String(item.id)}
            entityId={String(item.id)}
            orgId={orgId}
            avatar
            fallbackDisplayName={item.label}
          />
        );
      }

      const crmObject = targetObjectsMap.get(String(item.id));
      // Tags follow the control's scale: the compact `xs` control (inline
      // click-to-edit rows) takes `sm` pills — the same 20px as the read-mode
      // badges they replace — so the editor keeps the row's height on entry.
      const tagSize = size === 'xs' ? 'sm' : 'md';
      const displayLabel = crmObject ? getDisplayName(crmObject) : item.label;
      const leadingContent = crmObject ? (
        <CrmObjectAvatar object={crmObject} size='xxs' />
      ) : (
        <CrmObjectAvatar
          object={{
            id: String(item.id),
            org_id: orgId,
            entity_id: String(item.id),
            entity_type_name: entityType as EntityTypeName,
            properties: { ahoy_display_name: { value: item.label } },
            last_event_seq: 0,
            deleted_at: null,
          }}
          size='xxs'
        />
      );

      return (
        <BadgeWithButton
          key={String(item.id)}
          type='pill-color'
          color='gray'
          size={tagSize}
          leadingContent={leadingContent}
          onButtonClick={() => onRemove(item.id)}
          icon={XClose}
          buttonLabel={`Remove ${displayLabel}`}
        >
          <span>{displayLabel}</span>
        </BadgeWithButton>
      );
    },
    [targetObjectsMap, entityType, orgId, isSingleSelect, disabled, size]
  );

  // Only offer the GENERIC create option (createConfig) when the target
  // entity type's kind is creatable via the generic path (party/case/asset).
  // The onCustomCreate branch (e.g. secrets) is unaffected by this gate.
  const isCreatableTargetKind = isCreatableEntityKind(
    entityType,
    entityKindMap
  );

  // Show "Create new …" whenever a create config is resolvable (createConfig is
  // the curated static config OR the schema-derived fallback, so it is present
  // for ANY entity type) or a custom create handler is supplied, AND the user
  // has create permission on the target type. Gating on staticCreateConfig alone
  // would wrongly hide inline create for schema-driven/custom entity types.
  const showCreateOption =
    ((createConfig != null &&
      isCreatableTargetKind &&
      genericCreateTargetAllowed) ||
      onCustomCreate != null ||
      isFileUploadTarget) &&
    canCreateTargetObject &&
    !disabled &&
    !hideCreateOption &&
    !createMutation.isPending;

  const handleClear = useCallback(() => {
    // Clearing the value removes every currently selected target; evict any
    // that are drafts this input created so their seq-0 chips don't linger.
    for (const id of Array.isArray(value) ? value : []) {
      evictCreatedDraft(id);
    }
    onChange(null);
    notifySelectedObjects([]);
  }, [value, onChange, notifySelectedObjects, evictCreatedDraft]);

  // ---- File upload (ahoy_file targets) ----
  // The create modal can unmount mid-upload; guards state updates after that.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // The upload's async continuation must append the minted id to the selection
  // as it stands when the upload *resolves*, not as it stood when the file was
  // picked — a chip deselected mid-upload must not be silently restored.
  const valueRef = useLatestRef(value);

  const uploadAbortControllerRef = useRef<AbortController | null>(null);
  useEffect(() => {
    return () => {
      uploadAbortControllerRef.current?.abort();
    };
  }, []);

  const onFileSelected = useCallback(
    async (file: File) => {
      setUploadError(null);
      setIsUploading(true);
      const abortController = new AbortController();
      uploadAbortControllerRef.current = abortController;
      try {
        const uploaded = await fileService.uploadFile(orgId, file, {
          signal: abortController.signal,
        });
        const minted = await fileService.mintFileEntity(orgId, uploaded.id);
        const summary = buildOptimisticCrmObject({
          entityId: minted.entity_id,
          entityType: 'ahoy_file' as EntityTypeName,
          orgId,
          properties: {
            ahoy_filename: uploaded.original_filename,
            ahoy_mime_type: uploaded.mime_type,
            ahoy_file_size: String(uploaded.file_size),
            ahoy_display_name: uploaded.original_filename || 'Untitled File',
          },
          associations: [],
        });
        upsertSummaryObjects([summary]);
        if (!mountedRef.current) {
          return;
        }
        setFetchState(prev => ({
          ...prev,
          targetObjects: [summary, ...prev.targetObjects],
        }));
        const currentValue = Array.isArray(valueRef.current)
          ? [...valueRef.current]
          : [];
        onChange([...currentValue, minted.entity_id]);
      } catch (error) {
        logger.error(
          'File upload from association picker failed',
          error instanceof Error ? error : undefined,
          {
            component: 'crm',
            status: error instanceof FileUploadError ? error.status : undefined,
          }
        );
        if (mountedRef.current) {
          setUploadError("We couldn't upload the file. Try again.");
        }
      } finally {
        if (mountedRef.current) {
          setIsUploading(false);
        }
      }
    },
    [orgId, onChange, valueRef]
  );

  const { filePickerInput, openFilePicker } = useFilePicker({ onFileSelected });

  const handleFileUploadClick = useCallback(() => {
    if (isUploading) {
      return;
    }
    setUploadError(null);
    openFilePicker();
  }, [isUploading, openFilePicker]);

  // ---- Embedded variant (single-select) keyboard navigation ----
  // The options fetch filters server-side per keystroke, but its round trip
  // lags the typing — filter locally as well so the visible list (and what
  // Enter picks) always matches the query the user is looking at, with the
  // server response refining beyond the loaded page afterwards.
  const embeddedItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return items;
    }
    return items.filter(item =>
      (item.label ?? '').toLowerCase().includes(query)
    );
  }, [items, searchQuery]);

  // Single-select semantics, single-select look: the current value is shown
  // by checking its row (and a "No <type>" row for clearing) — never as a
  // removable chip, which reads as a multi-select.
  const embeddedSelectedId =
    isSingleSelect && Array.isArray(value) && value.length > 0
      ? String(value[0])
      : null;
  const embeddedRows = useMemo<
    ({ kind: 'clear' } | { kind: 'item'; item: SelectItemType })[]
  >(() => {
    const rows: ({ kind: 'clear' } | { kind: 'item'; item: SelectItemType })[] =
      embeddedItems.map(item => ({ kind: 'item', item }));
    // The clear row only exists for optional associations with a value set.
    if (embeddedSelectedId && allowClear) {
      rows.unshift({ kind: 'clear' });
    }
    return rows;
  }, [embeddedItems, embeddedSelectedId, allowClear]);
  const hasClearRow = embeddedRows[0]?.kind === 'clear';

  // The embedded option list is plain buttons (no popover, no react-aria
  // Menu), so arrow-key roving is managed manually from the search input.
  // The default highlight is the first pickable option, not the clear row —
  // Enter on open must never silently clear the association.
  const [activeIndex, setActiveIndex] = useState(hasClearRow ? 1 : 0);
  useEffect(() => {
    setActiveIndex(hasClearRow ? 1 : 0);
  }, [searchQuery, embeddedItems.length, hasClearRow]);
  // Keep the active row visible as arrow keys move it through the clipped list.
  useEffect(() => {
    containerRef.current
      ?.querySelector(`[data-embedded-option="${activeIndex}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleEmbeddedSearchKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // A disabled embedded picker is read-only: no roving, no Enter commit.
      if (disabled) {
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(i =>
          Math.min(i + 1, Math.max(embeddedRows.length - 1, 0))
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(i => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        const row = embeddedRows[activeIndex];
        if (row && (row.kind === 'clear' || !row.item.isDisabled)) {
          // Handled here — don't let the cell editor treat it as a commit
          // keystroke on top of the pick-commit this triggers.
          e.preventDefault();
          e.stopPropagation();
          if (row.kind === 'clear') {
            handleClear();
          } else {
            handleItemInserted(row.item.id);
          }
        }
      }
    },
    [disabled, embeddedRows, activeIndex, handleItemInserted, handleClear]
  );

  // Route the "create" affordance through the file-upload flow for
  // `ahoy_file` targets in every render variant (embedded, single-select
  // dropdown, and MultiSelect) — not just the MultiSelect path.
  const handleCreateOptionClick = isFileUploadTarget
    ? handleFileUploadClick
    : handleCreateClick;

  // Single-select: Dropdown trigger with icon + label + chevron
  if (isSingleSelect) {
    const selectedId =
      Array.isArray(value) && value.length > 0 ? value[0] : null;
    const selectedItem = items.find(i => i.id === selectedId);
    const triggerLabel =
      selectedItem?.label ||
      emptyLabel ||
      `Select ${associationType.label.toLowerCase()}`;

    // Embedded variant: no trigger, no popover — a search input with the
    // option list rendered directly into the parent surface (the cell-edit
    // card). The CURRENT value is communicated the single-select way: its
    // row is checked, and a "No <type>" row clears it — never a removable
    // chip, which reads as a multi-select. Rows mirror Dropdown.Item's inner
    // styling; roving highlight is manual (see handleEmbeddedSearchKeyDown).
    if (variant === 'embedded') {
      let embeddedList: React.ReactNode;
      if (isOptionsLoading) {
        embeddedList = (
          <div className='flex items-center justify-center py-4'>
            <LoadingIndicator size='2xs' />
          </div>
        );
      } else if (embeddedItems.length === 0) {
        embeddedList = (
          <div className='px-3 py-4 text-center text-sm text-tertiary'>
            No {pluralLabel} found
          </div>
        );
      } else {
        const clearLabel = `No ${entityTypeLabel?.toLowerCase() ?? associationType.label.toLowerCase()}`;
        embeddedList = embeddedRows.map((row, index) => {
          if (row.kind === 'clear') {
            return (
              <button
                key='__clear__'
                type='button'
                data-embedded-option={index}
                disabled={disabled}
                onClick={handleClear}
                onMouseEnter={() => setActiveIndex(index)}
                className={cx(
                  // Dropdown.Item's row shape at the table-cell scale: medium
                  // weight (not the standalone menu's semibold) and a tighter
                  // vertical rhythm so the options match the cell type.
                  'relative flex w-full items-center rounded-md px-2.5 py-1.5 text-left outline-hidden transition duration-100 ease-linear',
                  disabled ? 'cursor-not-allowed' : 'cursor-pointer',
                  index === activeIndex && 'bg-primary_hover'
                )}
              >
                <span className='flex grow items-center gap-2 truncate text-sm font-medium text-tertiary'>
                  <XClose
                    aria-hidden='true'
                    className='size-4 shrink-0 text-fg-quaternary'
                  />
                  <span className='truncate'>{clearLabel}</span>
                </span>
              </button>
            );
          }
          const { item } = row;
          const obj = targetObjectsMap.get(String(item.id));
          return (
            <button
              key={item.id}
              type='button'
              data-embedded-option={index}
              disabled={disabled || item.isDisabled}
              onClick={() => handleItemInserted(item.id)}
              onMouseEnter={() => setActiveIndex(index)}
              className={cx(
                'relative flex w-full items-center rounded-md px-2.5 py-1.5 text-left outline-hidden transition duration-100 ease-linear',
                item.id === selectedId && 'bg-active',
                index === activeIndex && 'bg-primary_hover',
                disabled || item.isDisabled
                  ? 'cursor-not-allowed'
                  : 'cursor-pointer'
              )}
            >
              <span
                className={cx(
                  'flex grow items-center gap-2 truncate text-sm font-medium',
                  item.isDisabled ? 'text-disabled' : 'text-secondary'
                )}
              >
                {obj && <CrmObjectAvatar object={obj} size='xxs' />}
                <span className='truncate'>{item.label}</span>
              </span>
              {item.id === selectedId && (
                <Check
                  aria-hidden='true'
                  className='ml-auto size-4 shrink-0 stroke-[2.5px] text-fg-brand-primary'
                />
              )}
            </button>
          );
        });
      }
      return (
        <div
          ref={containerRef}
          className={cx('flex w-full flex-col', containerClassName)}
        >
          {isFileUploadTarget && filePickerInput}
          {!createOnly && (
            <>
              <div className='px-2 pt-2 pb-1.5'>
                {/* `xs` (32px, text-sm) matches the compact option-row scale this
                    embedded picker renders inside a table cell. */}
                <InputField
                  icon={SearchLg}
                  size='xs'
                  autoFocus
                  isDisabled={disabled}
                  aria-label={`Search ${pluralLabel}`}
                  placeholder={
                    selectedItem
                      ? `${selectedItem.label} – type to replace…`
                      : `Search ${pluralLabel}`
                  }
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onKeyDown={handleEmbeddedSearchKeyDown}
                />
              </div>
              <div className='max-h-64 overflow-y-auto border-t border-secondary px-1.5 py-1'>
                {embeddedList}
              </div>
            </>
          )}
          {showCreateOption && !disabled && (
            <div className='w-full border-t border-secondary bg-primary px-1.5 py-1'>
              <Button
                color='tertiary'
                size='sm'
                iconLeading={Plus}
                onClick={handleCreateOptionClick}
                className='w-full justify-start'
              >
                {createOptionText}
              </Button>
            </div>
          )}
        </div>
      );
    }

    let dropdownContent: React.ReactNode;
    if (isOptionsLoading) {
      dropdownContent = (
        <div className='flex items-center justify-center py-4'>
          <LoadingIndicator size='2xs' />
        </div>
      );
    } else if (items.length === 0) {
      dropdownContent = (
        <div className='px-3 py-4 text-center text-sm text-tertiary'>
          No {pluralLabel} found
        </div>
      );
    } else {
      dropdownContent = (
        <Dropdown.Menu
          onAction={handleItemInserted}
          selectedKeys={selectedId ? new Set([selectedId]) : new Set()}
        >
          {items.map(item => {
            const obj = targetObjectsMap.get(String(item.id));
            return (
              <Dropdown.Item
                key={item.id}
                id={item.id}
                textValue={item.label}
                isDisabled={item.isDisabled}
              >
                <span className='flex items-center gap-2'>
                  {obj && <CrmObjectAvatar object={obj} size='xxs' />}
                  <span className='truncate'>{item.label}</span>
                </span>
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      );
    }

    const triggerSize = sizes[size];

    return (
      <div ref={containerRef} className={cx('relative', containerClassName)}>
        {isFileUploadTarget && filePickerInput}
        <Dropdown.Root isOpen={isOpen} onOpenChange={handleDropdownOpen}>
          {/* Focus ring while OPEN as well as on keyboard focus — the same
              rule as the library Select. Focus sits in the portaled popover
              while the picker is open, so a focus-visible-only ring would
              leave the trigger looking idle exactly when the user is working
              in it. */}
          <AriaButton
            isDisabled={disabled}
            aria-label={associationType.label}
            className={({ isFocusVisible }) =>
              cx(
                SELECT_TRIGGER_BASE,
                'gap-2 text-primary',
                triggerSize.root,
                (isOpen || isFocusVisible) && SELECT_TRIGGER_FOCUS,
                'disabled:cursor-not-allowed disabled:bg-disabled_subtle disabled:text-disabled',
                selectedId && !disabled && 'pr-8',
                triggerClassName
              )
            }
          >
            <Icon
              className={cx(triggerSize.icon, 'shrink-0 text-fg-quaternary')}
            />
            <span
              className={cx(
                'truncate text-left flex-1',
                !selectedItem && 'text-placeholder'
              )}
            >
              {triggerLabel}
            </span>
            {!selectedId || disabled ? (
              <ChevronDown
                aria-hidden='true'
                className={cx(
                  'ml-auto shrink-0 text-fg-quaternary',
                  CHEVRON_SIZE_CLASSES[size]
                )}
              />
            ) : null}
          </AriaButton>
          <Dropdown.Popover
            className='popover-element w-[var(--trigger-width)]'
            placement='bottom start'
          >
            {!createOnly && (
              <>
                <div className='px-2 pt-2 pb-1.5 border-b border-secondary'>
                  <InputField
                    icon={SearchLg}
                    size='sm'
                    autoFocus
                    aria-label={`Search ${pluralLabel}`}
                    placeholder={`Search ${pluralLabel}`}
                    value={searchQuery}
                    onChange={handleSearchInputChange}
                  />
                </div>
                {dropdownContent}
              </>
            )}
            {showCreateOption && (
              <div className='sticky bottom-0 w-full px-1.5 py-1 border-t border-secondary bg-primary'>
                <Button
                  color='tertiary'
                  size='sm'
                  iconLeading={Plus}
                  onClick={handleCreateOptionClick}
                  className='w-full justify-start'
                >
                  {createOptionText}
                </Button>
              </div>
            )}
          </Dropdown.Popover>
        </Dropdown.Root>
        {selectedId && !disabled && allowClear && (
          <div className='absolute right-2 top-1/2 -translate-y-1/2 z-10'>
            <Button
              color='tertiary'
              size='sm'
              iconLeading={XClose}
              className='!size-5 !p-0.5 [&_[data-icon]]:!size-3.5'
              aria-label={`Clear ${associationType.label.toLowerCase()}`}
              onClick={handleClear}
            />
          </div>
        )}
      </div>
    );
  }

  // MultiSelect (base/forms/select/multi-select.tsx) drops `className` from
  // its props in MultiSelectBase, so passing it directly does nothing. Wrap
  // in a container div to honour `containerClassName` for parity with the
  // single-select path above.
  return (
    <div className={containerClassName}>
      {isFileUploadTarget && filePickerInput}
      <MultiSelect
        placeholderIcon={Icon}
        placeholder={
          Array.isArray(value) && value.length > 0
            ? ''
            : `Search ${pluralLabel}`
        }
        size={size}
        items={availableItems}
        selectedItems={selectedItems}
        onItemInserted={handleItemInserted}
        onItemCleared={handleItemCleared}
        inputValue={searchQuery}
        onInputChange={setSearchQuery}
        isDisabled={disabled}
        shortcut={false}
        aria-label={associationType.label}
        maxSelection={maxSelection}
        renderTag={renderTag}
        onCreateNew={showCreateOption ? handleCreateOptionClick : undefined}
        createNewLabel={createOptionText}
        createOnly={createOnly}
        isLoading={isOptionsLoading}
        emptyText={`No ${pluralLabel} found`}
        onOpenChange={handleDropdownOpen}
      >
        {item => {
          const obj = targetObjectsMap.get(String(item.id));
          return (
            <MultiSelect.Item
              id={item.id}
              isDisabled={item.isDisabled}
              supportingText={item.supportingText}
              textValue={item.label}
            >
              <span className='flex items-center gap-2'>
                {obj && <CrmObjectAvatar object={obj} size='xxs' />}
                <span className='truncate'>{item.label}</span>
              </span>
            </MultiSelect.Item>
          );
        }}
      </MultiSelect>
      {isFileUploadTarget && isUploading && (
        <p className='text-body-small text-tertiary mt-1'>Uploading…</p>
      )}
      {uploadError && (
        <p className='text-body-small text-[var(--color-text-error-primary)] mt-1'>
          {uploadError}
        </p>
      )}
    </div>
  );
};
